import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * MacAppBadge — desktop counterpart to <AppStoreLink> and <PlayStoreLink>.
 *
 * Visually matches the official store badges (black pill, rounded-2xl, SVG
 * Apple/Windows mark + 2-line wordmark). Functionally it triggers the PWA
 * install prompt on Chromium-based desktop browsers; on Safari/macOS we
 * still render the badge — clicking it opens a soft toast with the simple
 * "File → Aggiungi al Dock" hint, so the visual lineup with iOS/Android
 * stays consistent in the Hero.
 *
 * Why a separate file (vs reusing DesktopPWAInstallButton)?
 *   - The existing button uses a gradient zinc style for the Header context.
 *   - The Hero wants the *official store-badge look* (mostly black). We keep
 *     the rich modal/perks UX inside DesktopPWAInstallButton for the dedicated
 *     /servizi/app-ufficiali page and use this minimal badge for the Hero.
 */
const APP_URL =
  typeof window !== "undefined" ? window.location.origin : "https://www.vitadahost.it";

function detect() {
  if (typeof window === "undefined") return { ok: false };
  const ua = navigator.userAgent || "";
  const isMobile =
    /Android|iPhone|iPad|iPod|Mobile|Phone|Tablet/i.test(ua) ||
    (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches);
  if (isMobile) return { ok: false };
  const isMac = /Mac|Macintosh/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  return { ok: true, isMac, isSafari, isStandalone };
}

export default function MacAppBadge({
  className = "",
  testid = "mac-app-badge",
  compact = false,
  // If true, always renders even on mobile (Hero wants the badge visible
  // everywhere — on phones it just opens a toast pointing to the desktop).
  alwaysVisible = true,
}) {
  const [info, setInfo] = useState(() => detect());
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const i = detect();
    setInfo(i);
    if (i.isStandalone) {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    const onInstalled = () => {
      setInstalled(true);
      deferredPromptRef.current = null;
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async (e) => {
    e.preventDefault();
    if (installed) return;
    const ev = deferredPromptRef.current;
    if (ev) {
      setInstalling(true);
      try {
        ev.prompt();
        const { outcome } = await ev.userChoice;
        if (outcome === "accepted") setInstalled(true);
        else toast("Installazione annullata. Puoi farla quando vuoi 👋");
      } catch {
        toast.error("Qualcosa è andato storto. Riprova fra poco.");
      } finally {
        deferredPromptRef.current = null;
        setInstalling(false);
      }
      return;
    }
    // No native prompt available (Safari/Firefox or already-installed-detection failed).
    // Best-effort hint, then route to the dedicated app page.
    if (info.isSafari && info.isMac) {
      toast("Dal menu Safari: File → Aggiungi al Dock", { duration: 5000 });
    } else if (!info.ok) {
      // Mobile: tell them to open on a computer
      toast("Apri www.vitadahost.it dal tuo computer per installare l'app desktop", {
        duration: 5000,
      });
    } else {
      toast("Apri questa pagina con Chrome o Edge per installarla in 1 click", {
        duration: 4500,
      });
    }
    window.location.href = "/servizi/app-ufficiali";
  };

  if (!info.ok && !alwaysVisible) return null;

  const heightClass = compact ? "h-12 md:h-14" : "h-14 md:h-16";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={installing}
      aria-label="Scarica l'app Vita da Host per Mac e Windows"
      data-testid={testid}
      className={`group inline-flex items-center justify-center rounded-2xl bg-black border border-white/15 hover:border-white/40 px-4 ${heightClass} transition-all hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-60 ${className}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 130 40"
        className="h-full w-auto"
        aria-hidden
        focusable="false"
      >
        {/* Computer / monitor mark — flat white outline */}
        <g transform="translate(4 7)" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1.2" y="1.2" width="22" height="15" rx="1.6" />
          <line x1="9" y1="20.5" x2="15.4" y2="20.5" />
          <line x1="12.2" y1="16.2" x2="12.2" y2="20.5" />
        </g>
        {/* Tiny "running" dot to imply "live app" */}
        <circle cx="20" cy="11" r="1.3" fill="#34d39a" />
        {/* "Disponibile su" micro-text */}
        <text
          x="32"
          y="15"
          fill="#fff"
          fontSize="7.5"
          fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
          letterSpacing="0.4"
        >
          Disponibile per
        </text>
        {/* "Mac & Windows" wordmark */}
        <text
          x="32"
          y="30"
          fill="#fff"
          fontSize="14"
          fontWeight="600"
          fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
          letterSpacing="-0.3"
        >
          Mac &amp; Windows
        </text>
      </svg>
    </button>
  );
}
